if (!customElements.get('ramayan-chapter-journey')) {
  class RamayanChapterJourney extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.chapters = Array.from(this.querySelectorAll('[data-chapter]'));
      if (!this.chapters.length) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.desktop = window.matchMedia('(min-width: 990px)');
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.stage = this.querySelector('[data-journey-stage]');
      this.header = document.querySelector('sticky-header');
      this.nav = this.querySelector('[data-chapter-nav]');
      this.counter = this.querySelector('[data-chapter-counter]');
      this.links = Array.from(this.querySelectorAll('[data-chapter-link]'));
      this.thread = this.querySelector('[data-thread-fill]');
      this.mobileProgress = this.querySelector('[data-mobile-progress]');
      this.current = -1;
      this.stageIndex = -1;
      this.stageRequest = 0;
      this.frame = 0;
      this.visible = true;
      this.onScroll = () => this.schedule();
      this.onResize = () => { this.measure(); this.schedule(); };
      this.classList.add('is-enhanced');
      this.preferences = () => {
        this.classList.toggle('is-reduced', this.reduced.matches || this.dataset.motion !== 'true');
        this.stage.getAnimations({ subtree: true }).forEach((animation) => animation.cancel());
        this.stageRequest += 1;
        this.stageIndex = -1;
        if (!this.desktop.matches) this.stage.replaceChildren();
        this.measure();
        this.schedule();
      };
      this.desktop.addEventListener('change', this.preferences, { signal });
      this.reduced.addEventListener('change', this.preferences, { signal });
      window.addEventListener('resize', this.onResize, { passive: true, signal });
      window.addEventListener('scroll', this.onScroll, { passive: true, signal });
      this.addEventListener('click', (event) => {
        const link = event.target.closest('[data-chapter-link], [data-journey-skip]');
        if (!link || !this.contains(link)) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const target = this.querySelector(link.getAttribute('href'));
        if (!target) return;
        event.preventDefault();
        this.nav.open = false;
        target.scrollIntoView({ block: 'start', behavior: this.motionEnabled() ? 'smooth' : 'instant' });
        (target.querySelector('.rj__title') || target).focus({ preventScroll: true });
      }, { signal });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.nav.open) {
          this.nav.open = false;
          this.nav.querySelector('summary').focus();
        }
      }, { signal });
      document.addEventListener('click', (event) => {
        if (!this.nav.contains(event.target)) this.nav.open = false;
      }, { signal });
      document.addEventListener('shopify:block:select', (event) => {
        const index = this.chapters.findIndex((chapter) => chapter.dataset.blockId === event.detail.blockId);
        if (index < 0) return;
        this.chapters[index].scrollIntoView({ block: 'start', behavior: 'instant' });
        this.setActive(index);
      }, { signal });
      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          this.visible = entries[0].isIntersecting;
          if (this.visible) { this.measure(); this.schedule(); }
        }, { rootMargin: '100px 0px' });
        this.visibilityObserver.observe(this);
      }
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.querySelector('.rj__chapters'));
        if (this.header) this.resizeObserver.observe(this.header);
      }
      this.preferences();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.visibilityObserver?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.stageRequest += 1;
      this.stage?.getAnimations({ subtree: true }).forEach((animation) => animation.cancel());
      this.classList.remove('is-enhanced');
    }

    motionEnabled() { return this.dataset.motion === 'true' && !this.reduced.matches; }

    measure() {
      if (this.header) this.style.setProperty('--rj-header-height', `${this.header.getBoundingClientRect().height}px`);
      this.positions = this.chapters.map((chapter) => {
        const rect = chapter.getBoundingClientRect();
        const node = chapter.querySelector('.rj__node').getBoundingClientRect();
        return { top: rect.top + window.scrollY, height: rect.height, node: node.top + window.scrollY + node.height / 2 };
      });
      const first = this.positions[0].node;
      const last = this.positions[this.positions.length - 1].node;
      const thread = this.thread.parentElement;
      const wrapperTop = this.querySelector('.rj__chapters').getBoundingClientRect().top + window.scrollY;
      thread.style.top = `${first - wrapperTop}px`;
      thread.style.height = `${Math.max(0, last - first)}px`;
      thread.style.bottom = 'auto';
    }

    schedule() {
      if (this.frame || !this.visible) return;
      this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); });
    }

    update() {
      if (!this.positions?.length) return;
      const viewport = window.innerHeight;
      const offset = parseFloat(getComputedStyle(this.querySelector(this.desktop.matches ? '.rj__showcase' : '.rj__mobile-nav')).top) || 0;
      const focus = window.scrollY + Math.max(offset + (this.desktop.matches ? 80 : 110), viewport * .42);
      let active = 0;
      this.positions.forEach((position, index) => { if (position.top <= focus) active = index; });
      this.setActive(active);
      const next = this.positions[active + 1];
      const segment = next ? Math.max(1, next.top - this.positions[active].top) : 1;
      const progressToNext = next ? Math.max(0, Math.min(1, (focus - this.positions[active].top) / segment)) : 0;
      const transition = Math.max(0, Math.min(1, (progressToNext - .7) / .3));
      const focusIndex = active + transition * transition * (3 - 2 * transition);
      const motion = this.motionEnabled();
      const first = this.positions[0].node;
      const last = this.positions[this.positions.length - 1].node;
      const progress = last === first ? 1 : Math.max(0, Math.min(1, (focus - first) / (last - first)));
      this.thread.style.transform = `scaleY(${progress})`;
      this.mobileProgress.style.transform = `scaleX(${(active + 1) / this.chapters.length})`;
      this.chapters.forEach((chapter, index) => {
        const distance = this.positions[index].top - window.scrollY;
        const reveal = motion ? Math.max(0, Math.min(1, (viewport - distance) / (viewport * .3))) : 1;
        const focusDistance = Math.abs(index - focusIndex);
        const focusOpacity = motion ? Math.max(this.desktop.matches ? .35 : .5, 1 / (1 + focusDistance * (this.desktop.matches ? .65 : .45))) : 1;
        const focusBlur = motion ? Math.min(this.desktop.matches ? 2.4 : 1.5, focusDistance * (this.desktop.matches ? .85 : .55)) : 0;
        chapter.style.setProperty('--rj-reveal', (.72 + .28 * reveal).toFixed(3));
        chapter.style.setProperty('--rj-lift', `${((1 - reveal) * 16).toFixed(1)}px`);
        chapter.style.setProperty('--rj-focus-opacity', focusOpacity.toFixed(3));
        chapter.style.setProperty('--rj-focus-blur', `${focusBlur.toFixed(2)}px`);
      });
    }

    setActive(index) {
      if (index !== this.current) {
        this.current = index;
        this.chapters.forEach((chapter, i) => {
          chapter.classList.toggle('is-active', i === index);
          chapter.classList.toggle('is-past', i < index);
          if (i === index) this.links[i].setAttribute('aria-current', 'step');
          else this.links[i].removeAttribute('aria-current');
        });
        this.counter.textContent = String(index + 1).padStart(2, '0');
      }
      if (this.desktop.matches && this.stageIndex !== index) this.showArtwork(index);
    }

    async showArtwork(index) {
      this.stageIndex = index;
      const request = ++this.stageRequest;
      const artwork = this.chapters[index].querySelector('[data-chapter-artifact]').cloneNode(true);
      const image = artwork.querySelector('img');
      if (image) {
        image.loading = 'eager';
        try { await image.decode(); } catch (_) {}
      }
      if (!this.isConnected || request !== this.stageRequest || !this.desktop.matches) return;
      this.stage.getAnimations({ subtree: true }).forEach((animation) => animation.cancel());
      const previous = this.stage.lastElementChild;
      this.stage.replaceChildren(...(previous ? [previous] : []), artwork);
      if (previous && this.motionEnabled()) {
        const animation = artwork.animate([
          { opacity: 0, transform: 'translateY(8px)' },
          { opacity: 1, transform: 'translateY(0)' }
        ], { duration: 420, easing: 'cubic-bezier(.2,.65,.3,1)' });
        try { await animation.finished; } catch (_) { return; }
        if (request === this.stageRequest) this.stage.replaceChildren(artwork);
      } else {
        this.stage.replaceChildren(artwork);
      }
    }
  }
  customElements.define('ramayan-chapter-journey', RamayanChapterJourney);
}
